import { trackAffiliateClick } from '../../../utils/analytics';

export default function Story() {
  return (
    <section className="py-12 px-4 pb-32">
      <div className="max-w-4xl mx-auto">
        {/* Opening */}
        <p className="text-gray-300 text-[22px] leading-relaxed mb-6">
          I am not a lawyer.
        </p>

        <p className="text-gray-300 text-[22px] leading-relaxed mb-6">
          I am a real estate investor who got tired of being pushed around by people who thought I wouldn't fight back.
        </p>

        <p className="text-gray-300 text-[22px] leading-relaxed mb-12">
          This is the story of how I stopped being a target.
        </p>

        {/* SEO H2 - Primary Keyword for Story Page */}
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-8 leading-tight">
          What to do when a contractor takes your money and doesn't finish the job.
        </h2>

        {/* The Contractor Story */}
        <h3 className="text-3xl md:text-4xl font-bold text-white mb-6 flex items-center gap-3">
          <i className="ri-hammer-fill text-[#DC2626]"></i>
          THE $4,500 HANDSHAKE
        </h3>

        {/* Dave Story - Quote Block Style */}
        <div className="border-l-4 border-[#DC2626] pl-6 mb-12 bg-[#0a0a0a]/30 py-4">
          <p className="text-gray-300 text-[22px] leading-relaxed mb-6">
            A few years ago, I hired a contractor named Dave to renovate a rental property.
          </p>

          <p className="text-gray-300 text-[22px] leading-relaxed mb-6">
            We shook hands. He seemed like a good guy. I gave him $4,500 upfront for materials.
          </p>

          <p className="text-gray-300 text-[22px] leading-relaxed mb-6">
            Two weeks later, the job was half-finished. Dave stopped returning my calls.
          </p>

          <p className="text-gray-300 text-[22px] leading-relaxed mb-6">
            I drove to the property. Tools were gone. Materials were gone. Dave was gone.
          </p>

          <p className="text-gray-300 text-[22px] leading-relaxed">
            I called a lawyer. He quoted me $7,500 to file a lawsuit. To recover $4,500.
          </p>
        </div>

        <p className="text-gray-300 text-[22px] leading-relaxed mb-6">
          That is when I realized something:
        </p>

        <p className="text-white text-[22px] font-bold leading-relaxed mb-12">
          The legal system is not designed for regular people. It is designed to protect people who can afford to use it.
        </p>

        {/* The Turning Point */}
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
          THE MOMENT EVERYTHING CHANGED
        </h2>

        <p className="text-gray-300 text-[22px] leading-relaxed mb-6">
          A few months later, I got into a dispute with an HOA.
        </p>

        <p className="text-gray-300 text-[22px] leading-relaxed mb-6">
          They were threatening to fine me $500/day for a landscaping issue that wasn't even my fault.
        </p>

        <p className="text-gray-300 text-[22px] leading-relaxed mb-6">
          This time, I didn't call a lawyer. I didn't negotiate. I didn't beg.
        </p>

        <p className="text-gray-300 text-[22px] leading-relaxed mb-6">
          I opened my phone, clicked a button, and had my <strong className="text-white">law firm send them a demand letter from a law firm</strong>.
        </p>

        <div className="bg-gradient-to-r from-[#DC2626]/20 to-transparent border-l-4 border-[#DC2626] p-6 rounded-r-lg mb-12">
          <p className="text-white text-2xl font-bold italic">
            "The fines stopped immediately. The HOA apologized. The whole thing was resolved in 48 hours."
          </p>
        </div>

        <p className="text-gray-300 text-[22px] leading-relaxed mb-6">
          Cost to me? $0.
        </p>

        <p className="text-gray-300 text-[22px] leading-relaxed mb-6">
          That is when I understood the game. <strong className="text-white">Legal help for contractor disputes</strong> and HOA battles doesn't have to cost thousands—if you have <strong className="text-white">affordable legal protection for homeowners</strong>.
        </p>

        {/* The Letterhead Loophole */}
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 flex items-center gap-3">
          <i className="ri-shield-star-fill text-[#DC2626]"></i>
          THE "LETTERHEAD" LOOPHOLE
        </h2>

        <p className="text-gray-300 text-[22px] leading-relaxed mb-6">
          That moment changed my entire business philosophy.
        </p>

        <p className="text-gray-300 text-[22px] leading-relaxed mb-6">
          I realized that in America, you aren't paying for the lawyer.
        </p>

        <p className="text-white text-[22px] font-bold leading-relaxed mb-6">
          You are paying for the Letterhead.
        </p>

        <p className="text-gray-300 text-[22px] leading-relaxed mb-6">
          That logo at the top of the page—that <strong className="text-white">lawyer demand letter to contractor</strong> or HOA board—sends a very specific signal to a bully:
        </p>

        <div className="bg-[#DC2626]/10 border border-[#DC2626] p-6 rounded-lg mb-12">
          <p className="text-white text-xl font-bold leading-relaxed">
            "This person is not a civilian. This person has a legal department. If I keep messing with them, it's going to cost me everything."
          </p>
        </div>

        <p className="text-gray-300 text-[22px] leading-relaxed mb-12">
          It flips the power dynamic instantly.
        </p>

        {/* The MLM Section - Grey Background Box */}
        <div className="bg-[#1a1a1a] border border-gray-700 rounded-lg p-8 mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            ADDRESSING THE ELEPHANT (THE MLM THING)
          </h2>

          <p className="text-gray-300 text-[22px] leading-relaxed mb-6">
            Now, we have to talk about the awkward part.
          </p>

          <p className="text-gray-300 text-[22px] leading-relaxed mb-6">
            If you Google LegalShield, you are going to see some noise.
          </p>

          <p className="text-gray-400 text-[22px] italic leading-relaxed mb-6">
            "It's an MLM." "It's a Pyramid Scheme." "My aunt tried to sell it to me at Thanksgiving."
          </p>

          <p className="text-gray-300 text-[22px] leading-relaxed mb-6">
            Here is my honest take:
          </p>

          <p className="text-white text-[22px] font-bold leading-relaxed mb-6">
            Yes. It is an MLM.
          </p>

          <p className="text-gray-300 text-[22px] leading-relaxed mb-6">
            The "Business Opportunity" side of it attracts some annoying people. The sales tactics can be cringe-worthy. The cancellation process requires a phone call (which is annoying).
          </p>

          <p className="text-white text-2xl font-black leading-relaxed mb-6">
            I do not care.
          </p>

          <p className="text-gray-300 text-[22px] leading-relaxed mb-6">
            I am not asking you to join a cult. I am not asking you to sell memberships to your neighbors.
          </p>

          <p className="text-gray-300 text-[22px] leading-relaxed mb-6">
            I am telling you that the Product—the actual access to the law firm—is the single most undervalued asset in the country. When you need to know <strong className="text-white">how to fight a bad contractor legally</strong> or deal with a <strong className="text-white">shady contractor who stole your money</strong>, this is the weapon.
          </p>

          <div className="space-y-4 my-8">
            <p className="text-gray-300 text-[22px] leading-relaxed">
              If you use it ONCE to fight a speeding ticket? <span className="text-white font-bold">It pays for itself.</span>
            </p>
            <p className="text-gray-300 text-[22px] leading-relaxed">
              If you use it ONCE to review a contract? <span className="text-white font-bold">It pays for itself.</span>
            </p>
            <p className="text-gray-300 text-[22px] leading-relaxed">
              If you send ONE letter that gets a contractor to finish the job? <span className="text-white font-bold">You have made a 1,000% return on investment.</span>
            </p>
          </div>
        </div>

        {/* Warning Section - Red Border */}
        <div className="bg-[#DC2626]/5 border-4 border-[#DC2626] rounded-lg p-8 mb-12">
          <div className="flex items-start gap-4 mb-6">
            <i className="ri-alert-fill text-4xl text-[#DC2626] flex-shrink-0"></i>
            <h2 className="text-3xl md:text-4xl font-bold text-[#DC2626]">
              A WARNING FOR MY INVESTORS
            </h2>
          </div>

          <p className="text-gray-300 text-[22px] leading-relaxed mb-6">
            However, there is a trap. And because I'm not a "LegalShield Rep," I'm going to tell you the truth.
          </p>

          <p className="text-white text-[22px] font-bold leading-relaxed mb-6">
            The Personal Plan ($29.95) does NOT cover your business.
          </p>

          <p className="text-gray-300 text-[22px] leading-relaxed mb-6">
            If you are a Landlord, an LLC owner, or an Entrepreneur, and you try to use the basic plan to evict a tenant or review a vendor contract... the lawyer will say "No."
          </p>

          <p className="text-gray-300 text-[22px] leading-relaxed mb-6">
            They will tell you it's a "Business Matter."
          </p>

          <p className="text-white text-[22px] font-bold leading-relaxed mb-6">
            Do not be cheap.
          </p>

          <p className="text-gray-300 text-[22px] leading-relaxed mb-6">
            If you have assets, you need to add the "Home Business Supplement" or get the "Small Business Plan."
          </p>

          <p className="text-gray-300 text-[22px] leading-relaxed mb-6">
            It costs a few dollars more. Pay it.
          </p>

          <p className="text-white text-[22px] font-bold leading-relaxed">
            That is the "Landlord Mode." That is what unlocks the coverage for your rentals, your LLCs, and your commercial disputes.
          </p>
        </div>

        {/* Choose Your Posture */}
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-8 text-center">
          CHOOSE YOUR POSTURE
        </h2>

        <p className="text-gray-300 text-[22px] leading-relaxed mb-12 text-center">
          You have two choices today.
        </p>

        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {/* Choice 1: The Victim */}
          <div className="bg-gray-800/30 border-2 border-gray-600 rounded-lg p-8">
            <h3 className="text-2xl font-bold text-gray-400 mb-6">Choice 1: The Victim.</h3>
            <div className="space-y-4">
              <p className="text-gray-400 text-[22px] leading-relaxed">
                You can continue to rely on "Good Faith." You can hope the contractor doesn't steal. You can hope the HOA acts reasonably. You can pay the traffic ticket because you "don't have time" to fight it.
              </p>
              <p className="text-gray-500 text-[22px] italic leading-relaxed">
                You can be the person the system is designed to exploit.
              </p>
            </div>
          </div>

          {/* Choice 2: The Owner */}
          <div className="bg-gradient-to-br from-[#DC2626]/20 to-[#DC2626]/5 border-2 border-[#DC2626] rounded-lg p-8">
            <h3 className="text-2xl font-bold text-[#DC2626] mb-6">Choice 2: The Owner.</h3>
            <div className="space-y-4">
              <p className="text-white text-[22px] leading-relaxed">
                You can walk through life with a Rottweiler on a chain.
              </p>
              <p className="text-white text-[22px] leading-relaxed">
                You can operate with the swagger of a billionaire who has General Counsel on speed dial.
              </p>
              <p className="text-white text-[22px] leading-relaxed">
                You can hit a button on your phone and unleash a law firm on anyone who tries to disrespect your time or your money.
              </p>
            </div>
          </div>
        </div>

        <p className="text-gray-300 text-[22px] leading-relaxed mb-6">
          I built BullyProofMyLife.com to give you the direct link to the tool I use.
        </p>

        <p className="text-gray-300 text-[22px] leading-relaxed mb-6">
          No recruiting. No sales pitch. Just the weapon.
        </p>

        <p className="text-white text-[22px] font-bold leading-relaxed mb-6">
          Get the membership.
        </p>

        <p className="text-white text-[22px] font-bold leading-relaxed mb-6">
          Get the business rider if you own rentals.
        </p>

        <p className="text-gray-300 text-[22px] leading-relaxed mb-12">
          And the next time someone tries to push you around...
        </p>

        <p className="text-white text-3xl font-black leading-relaxed mb-16 text-center">
          Push back.
        </p>

        {/* Signature Section */}
        <div className="flex flex-col items-center gap-6 pt-8 border-t border-gray-700">
          <img 
            src="https://static.readdy.ai/image/93c3e079cd1f297eadf6098c3b0ef55d/dadaf59f289a861c44051d9874a96e48.jpeg"
            alt="Matt Theriault"
            className="w-32 h-32 rounded-full object-cover object-top border-4 border-[#DC2626]"
          />
          <div className="text-center">
            <p className="text-white text-2xl font-bold mb-2">Matt Theriault</p>
            <p className="text-gray-400 text-lg mb-1">Founder, Epic Real Estate</p>
            <p className="text-gray-500 text-base">Desert Storm Veteran (USMC)</p>
          </div>
        </div>

        {/* NEW: End-of-Story CTA */}
        <div className="mt-16 bg-gradient-to-r from-[#DC2626] to-[#B91C1C] rounded-lg p-8 text-center">
          <p className="text-white text-xl sm:text-2xl font-bold mb-6 leading-relaxed">
            P.S. – I spent $4,500 learning that being "nice" doesn't work with bullies. You can learn that lesson for free right now.
          </p>
          <a 
            href="/#chooser"
            onClick={() => trackAffiliateClick('About Page CTA', '/#chooser', 'Story')}
            className="inline-flex items-center justify-center gap-2 sm:gap-3 bg-white hover:bg-gray-100 text-[#DC2626] font-black text-base sm:text-xl px-6 sm:px-10 py-4 sm:py-5 rounded-lg transition-all duration-300 shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer w-full sm:w-auto"
          >
            <span className="hidden sm:inline">TAKE ME TO THE OPTIONS</span>
            <span className="sm:hidden">VIEW OPTIONS</span>
            <i className="ri-arrow-right-line text-xl sm:text-2xl"></i>
          </a>
        </div>
      </div>
    </section>
  );
}
